import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link,
  Grid,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import api from '../utils/api';

export const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    marketingConsent: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const verificationInputRef = useRef<HTMLInputElement>(null);

  const handleBackToRegistration = () => {
    setEmailVerificationSent(false);
    setVerificationCode('');
    setError('');
    setCountdown(0);
  };

  // Get the redirect location from router state, or default to home
  const from = location.state?.from?.pathname || '/';

  // Countdown timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown]);

  // Focus verification input when it appears
  useEffect(() => {
    if (emailVerificationSent) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        if (verificationInputRef.current) {
          const inputElement = verificationInputRef.current.querySelector('input');
          if (inputElement) {
            inputElement.focus();
            inputElement.select(); // Also select the text for better UX
          }
        }
      }, 200); // Increased delay to ensure rendering
    }
  }, [emailVerificationSent]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    // If email verification hasn't been sent yet, send it first
    if (!emailVerificationSent) {
      setLoading(true);
      try {
        // Only send verification code, don't create user yet
        await sendVerificationCode();
        setEmailVerificationSent(true);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error sending verification code');
      } finally {
        setLoading(false);
      }
    } else {
      // If verification code is provided, verify it and create user
      if (verificationCode.length !== 5) {
        setError(t('auth.invalidCode'));
        return;
      }

      setVerificationLoading(true);
      try {
        // First verify the email code
        await api.post('/auth/verify-email-code', {
          email: formData.email,
          code: verificationCode
        });

        // Then create the user account
        await register({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phoneNumber: formData.phoneNumber || '', // Provide empty string as default
          marketingConsent: formData.marketingConsent
        });
        
        // Navigate to the original requested page or home
        navigate(from, { replace: true });
      } catch (err: any) {
        if (err.response?.data?.error?.includes('expired')) {
          setError(t('auth.codeExpired'));
        } else if (err.response?.data?.error?.includes('already exists')) {
          setError(t('auth.userAlreadyExists'));
        } else {
          setError(err.response?.data?.error || t('auth.invalidCode'));
        }
      } finally {
        setVerificationLoading(false);
      }
    }
  };

  const sendVerificationCode = async () => {
    try {
      await api.post('/auth/send-email-verification', { email: formData.email });
      setCountdown(300); // 5 minutes countdown
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error sending verification code');
    }
  };


  const handleResendCode = async () => {
    setResendLoading(true);
    setError('');
    
    try {
      await sendVerificationCode();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error sending verification code');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 64px)',
        padding: 2
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%'
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {t('auth.register')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {emailVerificationSent && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('auth.checkEmailForCode')}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('auth.firstName')}
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                disabled={loading || verificationLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('auth.lastName')}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                disabled={loading || verificationLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('auth.email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading || verificationLoading || emailVerificationSent}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('auth.phoneNumber')}
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
                disabled={loading || verificationLoading || emailVerificationSent}
                helperText={t('auth.phoneNumberOptional')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('auth.password')}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading || verificationLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('auth.confirmPassword')}
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={loading || verificationLoading}
              />
            </Grid>
            
            {emailVerificationSent && (
              <Grid item xs={12}>
                <TextField
                  ref={verificationInputRef}
                  fullWidth
                  label={t('auth.verificationCode')}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  required
                  disabled={verificationLoading}
                  autoFocus
                  inputProps={{
                    maxLength: 5,
                    style: { textAlign: 'center', fontSize: '24px', letterSpacing: '4px' }
                  }}
                  placeholder="00000"
                  helperText={t('auth.emailVerificationDescription')}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.marketingConsent}
                      onChange={handleChange}
                      name="marketingConsent"
                      disabled={loading || verificationLoading}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ textAlign: 'right', marginRight: 1 }}>
                      <Typography variant="body2">
                        {t('auth.marketingConsent')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('auth.marketingConsentOptional')}
                      </Typography>
                    </Box>
                  }
                  labelPlacement="start"
                />
              </Box>
            </Grid>
          </Grid>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={loading || verificationLoading || (emailVerificationSent && verificationCode.length !== 5)}
            sx={{ mt: 3 }}
          >
            {loading || verificationLoading ? (
              <CircularProgress size={24} />
            ) : emailVerificationSent ? (
              t('auth.verifyEmail')
            ) : (
              t('auth.register')
            )}
          </Button>

          {emailVerificationSent && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="text"
                onClick={handleResendCode}
                disabled={resendLoading || countdown > 0}
                sx={{ mb: 1, mr: 2 }}
              >
                {resendLoading ? (
                  <CircularProgress size={20} />
                ) : countdown > 0 ? (
                  `${t('auth.resendCode')} (${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')})`
                ) : (
                  t('auth.resendCode')
                )}
              </Button>
              <Button
                variant="text"
                onClick={handleBackToRegistration}
                disabled={verificationLoading}
                sx={{ mb: 1 }}
              >
                {t('auth.backToRegistration')}
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link component={RouterLink} to="/login" state={{ from: location.state?.from }}>
                {t('auth.login')}
              </Link>
            </Typography>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}; 